import React, { useEffect, useRef, useState } from "react";
import { Box, TextField, Tooltip, alpha, useTheme } from "@mui/material";

interface EditableSentenceProps {
  text: string;
  isDirty: boolean;
  /**
   * The AI's speaker label for this sentence is low-confidence. Highlights the
   * text (warning-tinted background + wavy underline) so the reviewer can spot
   * which assignments still need confirming.
   */
  isLowConfidence?: boolean;
  disabled?: boolean;
  onCommit: (newText: string) => void;
}

export const EditableSentence: React.FC<EditableSentenceProps> = ({
  text,
  isDirty,
  isLowConfidence = false,
  disabled = false,
  onCommit,
}) => {
  const theme = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(text);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isEditing) {
      setDraft(text);
    }
  }, [text, isEditing]);

  const enterEdit = () => {
    if (disabled) return;
    setDraft(text);
    setIsEditing(true);
  };

  const commit = () => {
    if (draft !== text) {
      onCommit(draft);
    }
    setIsEditing(false);
  };

  const cancel = () => {
    setDraft(text);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      cancel();
    } else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      commit();
    }
  };

  if (isEditing) {
    return (
      <TextField
        inputRef={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={handleKeyDown}
        autoFocus
        multiline
        fullWidth
        size="small"
        sx={{
          my: 0.25,
          "& .MuiOutlinedInput-root": {
            backgroundColor: alpha(theme.palette.primary.main, 0.04),
          },
        }}
      />
    );
  }

  // Dirty edits take visual precedence; otherwise flag low-confidence speaker
  // labels with a warning tint so the text itself is the review cue.
  const hoverBg = isDirty || isLowConfidence
    ? alpha(theme.palette.warning.main, 0.18)
    : alpha(theme.palette.primary.main, 0.08);

  const sentence = (
    <Box
      component="span"
      role="button"
      aria-label={
        isLowConfidence
          ? "Edit sentence (low-confidence speaker label)"
          : "Edit sentence"
      }
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? undefined : 0}
      onClick={disabled ? undefined : enterEdit}
      onKeyDown={
        disabled
          ? undefined
          : (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                enterEdit();
              }
            }
      }
      sx={{
        cursor: disabled ? "default" : "text",
        outline: "none",
        "&:focus-visible": disabled ? {} : { backgroundColor: hoverBg },
        px: 0.25,
        borderRadius: 0.5,
        borderBottom: isDirty
          ? `2px solid ${theme.palette.warning.main}`
          : "2px solid transparent",
        // A wavy underline (distinct from the dirty solid underline) marks the
        // unverified speaker label without competing with dirty highlighting.
        textDecoration:
          isLowConfidence && !isDirty
            ? `underline wavy ${theme.palette.warning.main}`
            : "none",
        textDecorationSkipInk: "none",
        textUnderlineOffset: "3px",
        backgroundColor:
          isDirty
            ? alpha(theme.palette.warning.main, 0.1)
            : isLowConfidence
              ? alpha(theme.palette.warning.main, 0.12)
              : "transparent",
        transition: "background-color 0.15s ease, border-color 0.15s ease",
        "&:hover": disabled ? {} : { backgroundColor: hoverBg },
      }}
    >
      {text}
    </Box>
  );

  if (isLowConfidence) {
    return (
      <Tooltip title="Low AI confidence in this sentence's speaker label — verify it's correct, or reassign.">
        {sentence}
      </Tooltip>
    );
  }
  return sentence;
};
